import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { questionsDb } from '../../lib/db/index.js';
import { useAuth } from '../../lib/firebase/auth-context.js';
import { questionInputSchema } from '../../lib/schemas/question.js';
import type { QuestionStatus } from '../../types/question.js';
import type { Visibility } from '../../types/visibility.js';
import { toAppError } from '../../lib/utils/error.js';
import { Spinner } from '../shared/Spinner.js';
import { MarkdownEditor } from '../shared/MarkdownEditor.js';
import { TagInput } from './TagInput.js';
import { VisibilityRadio } from './VisibilityRadio.js';
import { useDraft } from './useDraft.js';
import { DraftRestoredBanner } from './DraftRestoredBanner.js';
import { ProjectSelectField } from './ProjectSelectField.js';

export interface QuestionDraftState {
  title: string;
  body: string;
  tags: string[];
  visibility: Visibility;
  projectId?: string | undefined;
}

const INITIAL: QuestionDraftState = {
  title: '',
  body: '',
  tags: [],
  visibility: 'private',
};

interface QuestionFormProps {
  onDone: (id: string) => void;
  editId?: string | undefined;
  initialValues?: QuestionDraftState | undefined;
}

export function QuestionForm({ onDone, editId, initialValues }: QuestionFormProps) {
  const isEditing = !!editId;
  const { fbUser } = useAuth();
  const qc = useQueryClient();
  const draftHook = useDraft<QuestionDraftState>('question', INITIAL);
  const [editDraft, setEditDraft] = useState<QuestionDraftState>(initialValues ?? INITIAL);
  const draft = isEditing ? editDraft : draftHook.value;
  const setDraft = isEditing ? setEditDraft : draftHook.set;
  const clearDraft = isEditing ? () => {} : draftHook.clear;
  const wasRestored = isEditing ? false : draftHook.wasRestored;
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof QuestionDraftState>(k: K, v: QuestionDraftState[K]) =>
    setDraft({ ...draft, [k]: v });

  const m = useMutation({
    mutationFn: async () => {
      if (!fbUser) throw new Error('not signed in');
      const parsed = questionInputSchema.parse({
        title: draft.title,
        body: draft.body,
        status: '未回答',
        tags: draft.tags,
        visibility: draft.visibility,
        projectId: draft.projectId,
      });
      if (editId) {
        // status は編集モードでも触らない（解決済みなどに移行している可能性）
        await questionsDb.update(editId, {
          title: parsed.title,
          body: parsed.body,
          tags: parsed.tags,
          visibility: parsed.visibility as Visibility,
          projectId: parsed.projectId,
        });
        return editId;
      }
      return await questionsDb.create(fbUser.uid, {
        title: parsed.title,
        body: parsed.body,
        status: parsed.status as QuestionStatus,
        tags: parsed.tags,
        visibility: parsed.visibility as Visibility,
        projectId: parsed.projectId,
      });
    },
    onSuccess: async (id) => {
      clearDraft();
      await qc.invalidateQueries({ queryKey: ['questions'] });
      await qc.invalidateQueries({ queryKey: ['home', 'unansweredQuestions'] });
      await qc.invalidateQueries({ queryKey: ['projects', 'counts'] });
      await qc.invalidateQueries({ queryKey: ['project'] });
      if (editId) {
        await qc.refetchQueries({ queryKey: ['question', editId] });
      }
      onDone(id);
    },
    onError: (err) => setError(toAppError(err).userMessage),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    m.mutate();
  };

  return (
    <form className="compose-form" onSubmit={onSubmit}>
      {!isEditing && (
        <DraftRestoredBanner visible={wasRestored} onDiscard={clearDraft} />
      )}
      <label className="auth-field">
        <span>タイトル</span>
        <input
          required
          value={draft.title}
          onChange={(e) => set('title', e.target.value)}
          maxLength={200}
          placeholder="質問タイトル"
        />
      </label>
      <div className="auth-field">
        <span>本文</span>
        <MarkdownEditor
          value={draft.body}
          onChange={(v) => set('body', v)}
          rows={6}
          required
          maxLength={8000}
          placeholder="状況・試したこと・期待する結果..."
          ariaLabel="本文"
        />
      </div>
      <div className="auth-field">
        <span>タグ</span>
        <TagInput value={draft.tags} onChange={(t) => set('tags', t)} />
      </div>
      <ProjectSelectField
        value={draft.projectId}
        onChange={(v) => set('projectId', v)}
      />
      <VisibilityRadio
        value={draft.visibility}
        onChange={(v) => set('visibility', v)}
      />
      {error && <div className="auth-error">{error}</div>}
      <div className="compose-foot">
        <span className="mono compose-draft-hint">
          {isEditing ? '編集モード' : '下書きは自動保存されます'}
        </span>
        <button type="submit" className="btn btn-primary" disabled={m.isPending}>
          {m.isPending ? <Spinner /> : isEditing ? '更新' : '質問する'}
        </button>
      </div>
    </form>
  );
}
